import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import PageIcon from '@atlaskit/icon/core/page';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import type { Build } from '../../types';
import type { PlanCardProps } from '../index';
import { useSolutionPlan } from '../solution-plan-context';

import { messages } from './messages';

const ANIMATION_DURATION = '350ms'; // no token exists for this yet, it seems. { durations.medium } from '@atlaskit/motion' === 350ms

const styles = cssMap({
	container: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	miniIconButton: {
		transition: 'transform',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: ANIMATION_DURATION,
	},
	miniIconButton_opened: {
		transform: 'rotate(180deg)',
	},
});

export type CardHeaderProps = Pick<PlanCardProps, 'isLoading' | 'name' | 'solutionVersion'> & {
	isOpen: boolean;
	onToggle: () => void;
};

const getBuildCompletionPercentage = (builds: Build[]) => {
	if (builds.length === 0) {
		return 0;
	}
	return Math.round(
		(builds.filter((build) => build.state === 'SUCCESS').length / builds.length) * 100,
	);
};

export const CardHeader = ({
	isLoading,
	name,
	solutionVersion,
	isOpen,
	onToggle,
}: CardHeaderProps): React.JSX.Element => {
	di(useSolutionPlan);

	const { formatMessage } = useIntl();
	const { builds } = useSolutionPlan();
	const completion = getBuildCompletionPercentage(builds);

	return (
		<Flex justifyContent="space-between" xcss={styles.container}>
			<Flex alignItems="center" gap="space.100">
				<IconTile icon={PageIcon} label="File" appearance="orange" size="small" />
				<Flex direction="column">
					<Text>{name}</Text>
					<Text size="small" color="color.text.subtlest">
						{formatMessage(messages.planCardVersion, { solutionVersion })} &bull;{' '}
						{formatMessage(messages.planCardCompletion, { completion })}
					</Text>
				</Flex>
			</Flex>
			<Flex alignItems="center">
				{isLoading ? (
					<Spinner
						size="small"
						interactionName="studio-trigger-solution-build-plan-card-solution-loading"
						testId="studio-trigger-solution-build-plan-card.loading-indicator"
					/>
				) : (
					<IconButton
						icon={() => (
							<Box xcss={cx(styles.miniIconButton, isOpen && styles.miniIconButton_opened)}>
								<ChevronDownIcon
									label=""
									size="small"
									testId="studio-trigger-solution-build-plan-card.chevron-down-icon"
								/>
							</Box>
						)}
						label={formatMessage(isOpen ? messages.closeCardLabel : messages.openCardLabel)}
						appearance="subtle"
						spacing="compact"
						onClick={onToggle}
						testId="studio-trigger-solution-build-plan-card.open-close-card-icon-button"
					/>
				)}
			</Flex>
		</Flex>
	);
};
